import React, { useState } from "react";
import { Brain, Leaf, Sparkles, RotateCcw, ArrowRight, Sparkle } from "lucide-react";

const QUESTIONS = [
  {
    id: "q1",
    prompt: "It's 3pm on a busy day. What does your body crave most?",
    options: [
      { label: "Razor-sharp focus to power through my to-do list", value: "nootropic" },
      { label: "A deep exhale and a moment of calm", value: "calm" },
      { label: "A little spark of joy to lift the afternoon", value: "mood" },
    ],
  },
  {
    id: "q2",
    prompt: "Which everyday struggle hits closest to home?",
    options: [
      { label: "Brain fog and scattered attention", value: "nootropic" },
      { label: "Racing thoughts and tension I can't shake", value: "calm" },
      { label: "Low motivation and grey-day moods", value: "mood" },
    ],
  },
  {
    id: "q3",
    prompt: "Pick the ritual that sounds most like your ideal self.",
    options: [
      { label: "Deep work sessions in pure flow state", value: "nootropic" },
      { label: "Slow mornings, journaling, and breathwork", value: "calm" },
      { label: "Sunlit walks and feeling genuinely bright", value: "mood" },
    ],
  },
];

const RESULTS = {
  nootropic: {
    title: "The Cognitive Optimizer",
    aura: "Focus & Flow",
    icon: Brain,
    accent: "#8A9A86",
    blurb:
      "Your aura thrives on clarity. You'll love our nootropic blend of Lion's Mane, L-Theanine, and green tea actives — engineered for jitter-free, sustained mental sharpness.",
    botanicals: ["Lion's Mane Mushroom", "L-Theanine", "Green Tea Extract"],
  },
  calm: {
    title: "The Serene Soul",
    aura: "Serenity & Balance",
    icon: Leaf,
    accent: "#748470",
    blurb:
      "Your aura seeks stillness. Our calming elixir of KSM-66® Ashwagandha, Holy Basil, and marine magnesium gently lowers cortisol and restores your inner sanctuary.",
    botanicals: ["KSM-66® Ashwagandha", "Holy Basil", "Lemon Balm"],
  },
  mood: {
    title: "The Radiant Spirit",
    aura: "Vibrancy & Radiance",
    icon: Sparkles,
    accent: "#D4A373",
    blurb:
      "Your aura glows. Our mood-elevating blend of Iranian Saffron, Rhodiola Rosea, and active Vitamin D3 supports serotonin synthesis to keep your grey days bright.",
    botanicals: ["Iranian Saffron", "Rhodiola Rosea", "Vitamin D3"],
  },
};

export function AuraQuiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);

  const handleAnswer = (value) => {
    const nextAnswers = [...answers, value];
    if (step + 1 < QUESTIONS.length) {
      setAnswers(nextAnswers);
      setStep(step + 1);
    } else {
      // Tally winner
      const tally = nextAnswers.reduce((acc, v) => {
        acc[v] = (acc[v] || 0) + 1;
        return acc;
      }, {});
      const winner = Object.keys(tally).sort((a, b) => tally[b] - tally[a])[0];
      setAnswers(nextAnswers);
      setResult(winner);
    }
  };

  const reset = () => {
    setStep(0);
    setAnswers([]);
    setResult(null);
  };

  const progress = result ? 100 : Math.round((step / QUESTIONS.length) * 100);
  const current = QUESTIONS[step];

  return (
    <section id="quiz" className="py-24 md:py-32 bg-[#E9E1D6]" data-testid="aura-quiz-section">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="text-center space-y-4 mb-12">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9B84]">
            <Sparkle className="h-4 w-4 text-[#D4A373]" /> 60-Second Discovery
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-headings font-normal tracking-tight text-[#2A3B28]">
            What's Your Aura?
          </h2>
          <p className="text-base md:text-lg text-[#5C6B5A] leading-relaxed">
            Answer three quick questions and we'll match you to the InnerAura blend your body is craving.
          </p>
        </div>

        <div className="bg-[#FBF7F0] rounded-3xl border border-[#D7CCBE] shadow-sm p-8 md:p-12">
          {/* Progress bar */}
          <div className="mb-8">
            <div className="h-1.5 w-full bg-[#E9E1D6] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#8A9A86] rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
                data-testid="quiz-progress-bar"
              />
            </div>
          </div>

          {!result ? (
            <div className="space-y-8" data-testid="quiz-question">
              <div className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-[#D4A373]">
                  Question {step + 1} of {QUESTIONS.length}
                </span>
                <h3 className="text-xl sm:text-2xl font-headings font-normal text-[#2A3B28] leading-snug">
                  {current.prompt}
                </h3>
              </div>

              <div className="space-y-3">
                {current.options.map((opt, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleAnswer(opt.value)}
                    data-testid={`quiz-option-${idx}`}
                    className="group w-full text-left bg-[#F1EDE4] hover:bg-[#E9E1D6] border border-[#D7CCBE] rounded-2xl px-6 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-sm flex items-center justify-between gap-4"
                  >
                    <span className="text-sm md:text-base font-medium text-[#2A3B28]">
                      {opt.label}
                    </span>
                    <ArrowRight className="h-4 w-4 text-[#8A9B84] opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <AuraResult result={RESULTS[result]} onReset={reset} />
          )}
        </div>
      </div>
    </section>
  );
}

function AuraResult({ result, onReset }) {
  const Icon = result.icon;
  return (
    <div className="text-center space-y-7 animate-in fade-in duration-500" data-testid="quiz-result">
      <div className="space-y-3">
        <div
          className="mx-auto h-16 w-16 rounded-full flex items-center justify-center"
          style={{ backgroundColor: `${result.accent}22` }}
        >
          <Icon className="h-7 w-7" style={{ color: result.accent }} />
        </div>
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9B84] block">
          Your Aura is
        </span>
        <h3 className="text-3xl sm:text-4xl font-headings font-normal text-[#2A3B28]">
          {result.title}
        </h3>
        <p
          className="text-base font-semibold italic"
          style={{ color: result.accent }}
        >
          {result.aura}
        </p>
      </div>

      <p className="text-base text-[#5C6B5A] leading-relaxed max-w-xl mx-auto">
        {result.blurb}
      </p>

      <div className="flex flex-wrap justify-center gap-2">
        {result.botanicals.map((b, idx) => (
          <span
            key={idx}
            className="px-4 py-1.5 text-xs font-semibold rounded-full bg-[#E9E1D6] text-[#2A3B28] border border-[#D7CCBE]"
          >
            {b}
          </span>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
        <a
          href="#join"
          data-testid="quiz-result-join-button"
          className="bg-[#2A3B28] hover:bg-[#1C281B] text-white font-medium rounded-full px-8 py-3.5 transition-all duration-300 ease-out shadow-sm hover:shadow-md hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
        >
          Join the Aura List <ArrowRight className="h-4 w-4 text-[#D4A373]" />
        </a>
        <button
          onClick={onReset}
          data-testid="quiz-retake-button"
          className="bg-transparent hover:bg-[#E9E1D6] text-[#5C6B5A] font-medium rounded-full px-8 py-3.5 border border-[#D7CCBE] transition-all duration-300 inline-flex items-center justify-center gap-2"
        >
          <RotateCcw className="h-4 w-4" /> Retake Quiz
        </button>
      </div>
    </div>
  );
}
